import React, { forwardRef } from 'react';

import { Button } from '../../components/Button/Button';
import arrowUpRightIcon from '../../components/brand/icon-arrow-up-right.svg';
import type { JoinColumnData } from './joinSectionData';

type JoinColumnProps = {
    data: JoinColumnData;
    isHovered: boolean;
    isActive: boolean;
    isInactive: boolean;
    isVisible: boolean;
    // Desktop hover behavior uses section-level overlay videos; mobile uses
    // the column's own bg video. Skip rendering the column video on desktop
    // so the browser doesn't download / play a video that's display:none.
    showVideo: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

export const JoinColumn = forwardRef<HTMLDivElement, JoinColumnProps>(function JoinColumn(
    { data, isActive, isInactive, isVisible, showVideo, onMouseEnter, onMouseLeave },
    ref,
) {
    const classes = [
        'join-section__col',
        `join-section__col--${data.side}`,
        isActive && 'join-section__col--active',
        isInactive && 'join-section__col--inactive',
        isVisible && 'join-section__col--in-view',
    ].filter(Boolean).join(' ');

    const style = { backgroundImage: `url(${data.backgroundImage})` } as React.CSSProperties;

    return (
        <div ref={ref} className={classes} style={style}>
            {showVideo && (data.mobileHoverVideo || data.hoverVideo) && (
                <video
                    className="join-section__col-video"
                    src={data.mobileHoverVideo || data.hoverVideo}
                    poster={data.mobileBackgroundImage || data.backgroundImage || undefined}
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-hidden="true"
                />
            )}
            <div className="join-section__text">
                <h2 className="join-section__heading">{data.heading}</h2>
                <p className="join-section__subhead faves-eyebrow">{data.subhead}</p>
            </div>
            <div className="join-section__hover-text join-section__hover-text--stats">
                {data.stats.map((stat, idx) => (
                    <div key={idx} className="join-section__stat">
                        <div className="join-section__stat-value">{stat.value}</div>
                        <div className="join-section__stat-label faves-eyebrow">{stat.label}</div>
                    </div>
                ))}
            </div>
            <Button
                label={data.ctaLabel}
                href={data.ctaHref}
                variant="sliding"
                icon={<img src={arrowUpRightIcon} alt="" className="join-section__cta-icon" />}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
        </div>
    );
});
